import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setToken } from "../api/client";
import type { AcademicYear, Child, Term, User } from "../api/types";
import { pushEvent, setUser as setAnalyticsUser } from "../analytics/gtm";

type AuthState = {
  user: User | null;
  child: Child | null;
  children: Child[];
  year: AcademicYear | null;
  years: AcademicYear[];
  term: Term | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, childName: string) => Promise<void>;
  loginWithProvider: (
    provider: "google" | "apple",
    idToken: string,
    nonce?: string,
  ) => Promise<{ isNewUser: boolean }>;
  completeSession: (accessToken: string) => Promise<void>;
  logout: () => void;
  setTermId: (termId: number) => void | Promise<void>;
  setChildId: (childId: number) => void | Promise<void>;
  refreshContext: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [child, setChild] = useState<Child | null>(null);
  const [childrenList, setChildrenList] = useState<Child[]>([]);
  const [years, setYears] = useState<AcademicYear[]>([]);
  const [year, setYear] = useState<AcademicYear | null>(null);
  const [term, setTerm] = useState<Term | null>(null);
  const [loading, setLoading] = useState(true);

  const loadHousehold = useCallback(async (preferredChildId?: number | null) => {
    const me = await api.me();
    setUser(me);
    const kids = await api.listChildren();
    setChildrenList(kids);
    const rawChildId =
      preferredChildId != null
        ? String(preferredChildId)
        : localStorage.getItem("childId");
    const savedChildId =
      rawChildId != null && rawChildId !== "" ? Number(rawChildId) : NaN;
    const activeChild =
      (Number.isFinite(savedChildId)
        ? kids.find((c) => c.id === savedChildId)
        : null) ??
      kids[0] ??
      null;
    setChild(activeChild);
    if (activeChild) {
      localStorage.setItem("childId", String(activeChild.id));
    } else {
      localStorage.removeItem("childId");
    }
    // Academic years are shared across the household (all children).
    const yearList = await api.listYears();
    setYears(yearList);
    if (!activeChild && yearList.length === 0) {
      setYear(null);
      setTerm(null);
      return;
    }
    const savedTermId = localStorage.getItem("termId");
    let selectedYear = yearList.find((y) => y.is_active) ?? yearList[0] ?? null;
    let selectedTerm: Term | null = null;
    if (savedTermId) {
      for (const y of yearList) {
        const match = y.terms.find((t) => String(t.id) === savedTermId);
        if (match) {
          selectedYear = y;
          selectedTerm = match;
          break;
        }
      }
    }
    if (!selectedTerm && selectedYear) {
      selectedTerm = selectedYear.terms[0] ?? null;
    }
    setYear(selectedYear);
    setTerm(selectedTerm);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    loadHousehold()
      .catch(() => {
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [loadHousehold]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { access_token } = await api.login(email, password);
      setToken(access_token);
      await loadHousehold();
    },
    [loadHousehold],
  );

  const register = useCallback(
    async (email: string, password: string, childName: string) => {
      const { access_token } = await api.register(email, password, childName);
      setToken(access_token);
      await loadHousehold();
    },
    [loadHousehold],
  );

  const loginWithProvider = useCallback(
    async (provider: "google" | "apple", idToken: string, nonce?: string) => {
      const result =
        provider === "google"
          ? await api.googleLogin(idToken)
          : await api.appleLogin(idToken, nonce);
      setToken(result.access_token);
      await loadHousehold();
      return { isNewUser: Boolean(result.is_new_user) };
    },
    [loadHousehold],
  );

  const completeSession = useCallback(
    async (accessToken: string) => {
      setToken(accessToken);
      await loadHousehold();
    },
    [loadHousehold],
  );

  const logout = useCallback(() => {
    pushEvent("logout");
    setAnalyticsUser(null);
    setToken(null);
    setUser(null);
    setChild(null);
    setChildrenList([]);
    setYears([]);
    setYear(null);
    setTerm(null);
    localStorage.removeItem("childId");
    localStorage.removeItem("termId");
  }, []);

  const setChildId = useCallback(
    async (childId: number) => {
      if (!Number.isFinite(childId)) return;
      localStorage.setItem("childId", String(childId));
      // Keep the selected term — academic years are shared across children.
      await loadHousehold(childId);
    },
    [loadHousehold],
  );

  const setTermId = useCallback(
    async (termId: number) => {
      if (!Number.isFinite(termId)) return;

      const matchYear = years.find((y) => y.terms.some((t) => t.id === termId));
      if (matchYear) {
        const match = matchYear.terms.find((t) => t.id === termId)!;
        setYear(matchYear);
        setTerm(match);
        localStorage.setItem("termId", String(termId));
        return;
      }

      const yearList = await api.listYears();
      setYears(yearList);
      for (const y of yearList) {
        const match = y.terms.find((t) => t.id === termId);
        if (match) {
          setYear(y);
          setTerm(match);
          localStorage.setItem("termId", String(termId));
          return;
        }
      }
    },
    [years],
  );

  const value = useMemo(
    () => ({
      user,
      child,
      children: childrenList,
      year,
      years,
      term,
      loading,
      login,
      register,
      loginWithProvider,
      completeSession,
      logout,
      setTermId,
      setChildId,
      refreshContext: () => loadHousehold(),
    }),
    [
      user,
      child,
      childrenList,
      year,
      years,
      term,
      loading,
      login,
      register,
      loginWithProvider,
      completeSession,
      logout,
      setTermId,
      setChildId,
      loadHousehold,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
